import { useCallback } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';

/** The Clients route's search: `?text=<bulkSendId>` opens the Text focus view over the list. */
export interface TextClientsSearch {
  text?: string;
}

/** A bulk send id (a cuid). Anything else — `../`, `%2F`, spaces — never reaches an API path. */
const BULK_SEND_ID = /^[A-Za-z0-9_-]{1,64}$/;

/** Keep a usable bulk send id and drop anything else (a number, an empty value, junk from an edited URL). */
export function validateTextClientsSearch(search: Record<string, unknown>): TextClientsSearch {
  const { text } = search;
  return typeof text === 'string' && BULK_SEND_ID.test(text) ? { text } : {};
}

/** History-entry flag: this `?text=` entry was pushed by the app, so the entry before it is the bare list. */
const OPENED_IN_APP = 'textClientsOpenedInApp';

/**
 * The history state of a `?text=` entry the app opens (pass it to a `<Link state>` too): closing the view then
 * steps back to the page it was opened from instead of replacing the entry.
 */
export function markTextViewOpenedInApp<T>(previous: T): T {
  // `HistoryState` lives in @tanstack/history, which this app can't augment; the flag is read back below.
  return { ...previous, [OPENED_IN_APP]: true } as T;
}

/**
 * Open / close the Text focus view. Opening pushes a history entry, so the browser Back button closes it.
 * Closing goes back to that entry when the app pushed it (no dead "Back" press left behind); a view reached
 * from a link or a fresh tab replaces its own entry with the bare list instead of leaving the app.
 *
 * `orgRouteParam` is the URL's `$orgId` segment (the salon short code), not `useOrg().orgId`.
 */
export function useTextClientsFocusViewSearch(orgRouteParam: string) {
  const navigate = useNavigate();
  const router = useRouter();

  const open = useCallback(
    (bulkSendId: string) =>
      navigate({
        to: '/$orgId/clients',
        params: { orgId: orgRouteParam },
        search: { text: bulkSendId },
        state: markTextViewOpenedInApp,
      }),
    [navigate, orgRouteParam]
  );

  const close = useCallback(() => {
    if (Reflect.get(router.state.location.state, OPENED_IN_APP) === true) {
      router.history.back();
      return;
    }
    void navigate({
      to: '/$orgId/clients',
      params: { orgId: orgRouteParam },
      search: ({ text: _text, ...rest }: TextClientsSearch) => rest,
      replace: true,
    });
  }, [navigate, orgRouteParam, router]);

  return { open, close };
}
